import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { api, resolveAsset } from '../api/client';
import { CoverTitle } from './BookViewer';
import { useT, useLang } from '../i18n';

// Homepage "browse a real book" rail. Until now the only thing a visitor could
// see was cover art on the catalog cards — never the inside of a book, which is
// the actual product. Each card opens that story's shareable /demo flip-through
// (the whole book, watermarked), so the section answers "what am I buying?"
// rather than showing yet another row of covers.
//
// The books are our own samples, curated server-side (see server/data/showcase.js).
// The printed front panel is 200×210mm; at 96dpi that's the pixel stage the
// cover title lays out in. The card renders that full-size stage and scales it
// down, exactly like the admin cover preview — so the title lands at the same
// spot, size and colours it has on the real book instead of being re-flowed.
const PANEL_W_PX = (200 * 96) / 25.4;
const PANEL_H_PX = (210 * 96) / 25.4;
const CARD_W_PX = 168;

export default function StoryShowcase() {
  const t = useT();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const railRef = useRef(null);
  // Arrow affordances are hidden at the ends of the rail (and entirely when
  // everything already fits, e.g. a wide desktop with few books).
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    api.get('/api/showcase').then((d) => setItems(d.items || [])).catch(() => {});
  }, []);

  // The rail is RTL, so "scrollLeft" runs negative in the reading direction —
  // normalise it so the same end-detection works in both directions.
  const syncArrows = () => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft);
    setAtStart(pos < 8);
    setAtEnd(max - pos < 8);
  };

  useEffect(() => {
    syncArrows();
    const el = railRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    return () => {
      el.removeEventListener('scroll', syncArrows);
      window.removeEventListener('resize', syncArrows);
    };
  }, [items.length]);

  const nudge = (dir) => {
    const el = railRef.current;
    if (!el) return;
    // One "page" of cards, capped so a wide screen doesn't jump the whole rail.
    const step = Math.min(el.clientWidth * 0.8, 640);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (items.length === 0) return null;

  return (
    <section className="home-section showcase-section">
      <h2 className="section-title">{t.showcaseTitle}</h2>
      <p className="showcase-subtitle">{t.showcaseSubtitle}</p>

      <div className="showcase-rail-wrap">
        <button
          type="button"
          className="showcase-arrow showcase-arrow-prev"
          onClick={() => nudge(1)}
          aria-label={t.showcasePrev}
          hidden={atStart}
        >
          <ChevronRight size={22} aria-hidden="true" />
        </button>

        <div className="showcase-rail" ref={railRef}>
          {items.map((s) => (
            <a
              key={s.shareToken}
              href={`/read/${s.shareToken}`}
              className="showcase-card"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="showcase-book">
                <img src={resolveAsset(s.coverImageUrl)} alt={s.title} loading="lazy" />
                {s.title && (
                  <div
                    className="showcase-title-stage"
                    style={{
                      width: `${PANEL_W_PX}px`,
                      height: `${PANEL_H_PX}px`,
                      transform: `scale(${CARD_W_PX / PANEL_W_PX})`,
                    }}
                    aria-hidden="true"
                  >
                    <CoverTitle
                      title={s.title}
                      titleColors={s.coverTitleColors}
                      titleTheme={s.coverTitleTheme === 'light' ? 'light' : 'dark'}
                      titleStyle={s.coverTitleStyle}
                      position={s.coverTitlePosition}
                    />
                  </div>
                )}
                <span className="showcase-book-spine" aria-hidden="true" />
                <span className="showcase-open-badge">
                  <BookOpen size={15} aria-hidden="true" /> {t.showcaseOpen}
                </span>
              </div>
              <span className="showcase-theme">{lang === 'he' ? s.themeHe : s.themeAr}</span>
              {s.pages > 0 && (
                <span className="showcase-pages">{s.pages} {t.showcasePages}</span>
              )}
            </a>
          ))}
        </div>

        <button
          type="button"
          className="showcase-arrow showcase-arrow-next"
          onClick={() => nudge(-1)}
          aria-label={t.showcaseNext}
          hidden={atEnd}
        >
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
